/**
 * <collection-card-filter>
 *
 * Turns the Collection cards inside a custom-content-v2 section into an in-page
 * filter for the collection page's product grid. Clicking a card fetches that
 * collection's grid and swaps it into #ProductGridContainer without navigating
 * away, so the shopper stays on the current collection URL (e.g. /collections/brushes).
 *
 * Progressive enhancement: each card keeps its real href, so with JS disabled
 * (or on error) the click simply follows the link to the collection.
 *
 * Opt-in via the section setting `collection_cards_filter_grid`, which renders
 * this element and adds `data-collection-filter="<handle>"` to each card link.
 */
if (!customElements.get('collection-card-filter')) {
  class CollectionCardFilter extends HTMLElement {
    constructor() {
      super();
      this.GRID_ID = 'ProductGridContainer';
      this.COUNT_ID = 'ProductCount';
      this.LOADING_CLASS = 'cc-grid-loading';
      this.cache = new Map();
      this.inflight = new Map();
      this.activeHandle = null;
      this.requested = null;

      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onTriggerWarm = this.onTriggerWarm.bind(this);
      this.onPopState = this.onPopState.bind(this);
    }

    connectedCallback() {
      this.basePath = window.location.pathname;
      this.triggers = Array.from(this.querySelectorAll('[data-collection-filter]'));
      if (!this.triggers.length) return;

      // Snapshot the server-rendered (unfiltered) grid so we can restore it
      // without a network round-trip.
      const grid = this.grid;
      const count = this.count;
      this.originalGrid = grid ? grid.innerHTML : null;
      this.originalCount = count ? count.innerHTML : null;

      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', this.onTriggerClick);
        // Warm this sub-category's grid on intent (hover / touch-start) instead
        // of eagerly prefetching ALL sub-categories on load — the eager prefetch
        // fired a 3x full-page fetch + main-thread parse storm that janked the
        // Brushes page (and its carousel scroll). A click without a prior warm
        // still works via fetch-on-click, which shows the loading state.
        trigger.addEventListener('pointerenter', this.onTriggerWarm);
        trigger.addEventListener('touchstart', this.onTriggerWarm, { passive: true });
      });
      window.addEventListener('popstate', this.onPopState);

      // Mouse drag-to-scroll for the mobile/tablet card carousel.
      this.track = this.querySelector('.custom-content-v2__row');
      if (this.track) this.setupDrag();

      // A11y: live region for announcing filtered results; reduced-motion pref.
      this.status = this.querySelector('[data-cc-status]');
      this.reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // Honour a deep link / refresh on ?category=<handle>.
      const initial = this.handleFromUrl();
      if (initial && this.hasTrigger(initial)) {
        this.applyFilter(initial, { updateHistory: false });
      }
    }

    disconnectedCallback() {
      (this.triggers || []).forEach((trigger) => {
        trigger.removeEventListener('click', this.onTriggerClick);
        trigger.removeEventListener('pointerenter', this.onTriggerWarm);
        trigger.removeEventListener('touchstart', this.onTriggerWarm);
      });
      window.removeEventListener('popstate', this.onPopState);
    }

    get grid() {
      return document.getElementById(this.GRID_ID);
    }

    get count() {
      return document.getElementById(this.COUNT_ID);
    }

    handleFromUrl() {
      return new URLSearchParams(window.location.search).get('category');
    }

    hasTrigger(handle) {
      return this.triggers.some((trigger) => trigger.dataset.collectionFilter === handle);
    }

    onTriggerClick(event) {
      // Swallow the click that trails a real drag so dragging never filters.
      if (this.suppressClick) {
        this.suppressClick = false;
        event.preventDefault();
        return;
      }
      const trigger = event.currentTarget;
      const handle = trigger.dataset.collectionFilter;
      if (!handle || !this.grid) return; // no grid on page -> let the link work
      event.preventDefault();
      // Slide the clicked card fully into view (e.g. the peeking third on tablet).
      this.scrollCardIntoView(trigger);
      if (handle === this.activeHandle) return;
      this.applyFilter(handle, { updateHistory: true });
    }

    // Warm one sub-category's grid on hover / touch-start so the click that
    // follows is a cache hit — without the load-time prefetch storm.
    onTriggerWarm(event) {
      const handle = event.currentTarget && event.currentTarget.dataset.collectionFilter;
      if (handle && !this.cache.has(handle)) this.fetchPayload(handle).catch(() => {});
    }

    onPopState() {
      const handle = this.handleFromUrl();
      if (handle && this.hasTrigger(handle)) {
        this.applyFilter(handle, { updateHistory: false });
      } else {
        this.restoreOriginal({ updateHistory: false });
      }
    }

    applyFilter(handle, options) {
      const settings = options || {};
      if (!this.grid) return;
      this.requested = handle;

      // Instant path — the grid was preloaded (or previously viewed).
      if (this.cache.has(handle)) {
        this.swap(this.cache.get(handle), handle, settings.updateHistory);
        return;
      }

      // Not warmed yet: show a loading state, then fetch (or join an in-flight
      // prefetch) and swap when it lands — unless a newer selection supersedes it.
      this.grid.classList.add(this.LOADING_CLASS);
      this.grid.setAttribute('aria-busy', 'true');
      this.fetchPayload(handle)
        .then((payload) => {
          if (this.requested !== handle) return; // superseded by a later click
          this.swap(payload, handle, settings.updateHistory);
        })
        .catch(() => {
          // Fall back to a normal navigation so the click is never a dead end.
          if (this.requested === handle) window.location.href = `/collections/${handle}`;
        });
    }

    // Fetch a sub-collection's grid once and cache it. Concurrent callers (e.g.
    // an idle prefetch and a click) share the same in-flight request.
    fetchPayload(handle) {
      if (this.cache.has(handle)) return Promise.resolve(this.cache.get(handle));
      if (this.inflight.has(handle)) return this.inflight.get(handle);

      const promise = fetch(`/collections/${encodeURIComponent(handle)}`, { credentials: 'include' })
        .then((response) => {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then((responseText) => {
          const parsed = new DOMParser().parseFromString(responseText, 'text/html');
          const newGrid = parsed.getElementById(this.GRID_ID);
          if (!newGrid) throw new Error('grid-not-found');
          const newCount = parsed.getElementById(this.COUNT_ID);
          const payload = {
            grid: newGrid.innerHTML,
            count: newCount ? newCount.innerHTML : null,
            responseText: responseText,
          };
          this.cache.set(handle, payload);
          this.inflight.delete(handle);
          return payload;
        })
        .catch((error) => {
          this.inflight.delete(handle);
          throw error;
        });

      this.inflight.set(handle, promise);
      return promise;
    }

    // Pointer drag-to-scroll for the horizontal card carousel (mouse + pen).
    // Touch is left to native scrolling (compositor-threaded, already smooth);
    // desktop (non-overflowing row) is a no-op. On release the drag carries
    // velocity-tracked momentum so it glides like a native flick instead of
    // stopping dead — reduced-motion users get an instant stop.
    setupDrag() {
      const track = this.track;
      const THRESHOLD = 6;
      const MAX_V = 4; // px/ms cap so a fast fling can't rocket across
      let down = false;
      let startX = 0;
      let startLeft = 0;
      let moved = false;
      let lastX = 0;
      let lastT = 0;
      let velocity = 0; // px/ms; sign follows pointer direction
      let momentumId = null;

      const maxScroll = () => track.scrollWidth - track.clientWidth;
      const stopMomentum = () => {
        if (momentumId) {
          cancelAnimationFrame(momentumId);
          momentumId = null;
        }
      };
      const settle = () => track.classList.remove('cc-dragging');

      // Decelerating rAF glide after release. Snap (re-enabled by settle) then
      // eases to the nearest card once the glide comes to rest.
      const glide = () => {
        const friction = 0.94;
        const step = () => {
          velocity *= friction;
          const next = Math.max(0, Math.min(maxScroll(), track.scrollLeft - velocity * 16));
          track.scrollLeft = next;
          const stuck = next <= 0 || next >= maxScroll();
          if (Math.abs(velocity) < 0.02 || stuck) {
            momentumId = null;
            settle();
            return;
          }
          momentumId = requestAnimationFrame(step);
        };
        momentumId = requestAnimationFrame(step);
      };

      // Document-level move/up + NO setPointerCapture, and engage "drag mode"
      // (cc-dragging / suppressClick) only after real movement. This keeps a
      // plain click flowing straight to the filter handler — pointer capture and
      // an eager pointer-events lock were swallowing the click and letting the
      // card's href navigate instead of filtering in place.
      const onMove = (event) => {
        if (!down) return;
        const dx = event.clientX - startX;
        if (!moved && Math.abs(dx) > THRESHOLD) {
          moved = true;
          track.classList.add('cc-dragging');
        }
        if (!moved) return;
        const now = event.timeStamp || performance.now();
        const dt = now - lastT;
        if (dt > 0) velocity = velocity * 0.7 + ((event.clientX - lastX) / dt) * 0.3; // smoothed
        lastX = event.clientX;
        lastT = now;
        track.scrollLeft = startLeft - dx;
        event.preventDefault();
      };
      const onUp = () => {
        if (!down) return;
        down = false;
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        document.removeEventListener('pointercancel', onUp);
        if (!moved) return;
        this.suppressClick = true; // swallow the click that trails a real drag
        const v = Math.max(-MAX_V, Math.min(MAX_V, velocity));
        if (!this.reduceMotion && Math.abs(v) > 0.05) {
          velocity = v;
          glide();
        } else {
          settle();
        }
      };

      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch') return; // native touch scrolling
        if (event.button !== undefined && event.button !== 0) return; // primary button only
        if (maxScroll() <= 1) return; // not a carousel (desktop)
        stopMomentum();
        track.classList.remove('cc-dragging'); // clear leftover from an interrupted glide
        down = true;
        moved = false;
        velocity = 0;
        this.suppressClick = false;
        startX = event.clientX;
        startLeft = track.scrollLeft;
        lastX = event.clientX;
        lastT = event.timeStamp || performance.now();
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
        document.addEventListener('pointercancel', onUp);
      });
      // Kill the native image-drag ghost mid-drag.
      track.addEventListener('dragstart', (event) => { if (down) event.preventDefault(); });
    }

    // Slide a card fully into view within the carousel (no page scroll).
    scrollCardIntoView(trigger) {
      const track = this.track;
      if (!track || track.scrollWidth <= track.clientWidth + 1) return;
      const card = trigger.closest('[id^="block-"]') || trigger;
      const trackRect = track.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      let delta = 0;
      if (cardRect.right > trackRect.right) {
        delta = cardRect.right - trackRect.right + 8; // reveal the peeking right edge
      } else if (cardRect.left < trackRect.left) {
        delta = cardRect.left - trackRect.left - 8;
      }
      if (delta !== 0) track.scrollBy({ left: delta, behavior: this.reduceMotion ? 'auto' : 'smooth' });
    }

    restoreOriginal(options) {
      const settings = options || {};
      if (!this.grid || this.originalGrid === null) return;
      this.swap({ grid: this.originalGrid, count: this.originalCount, responseText: null }, null, settings.updateHistory);
    }

    swap(payload, handle, updateHistory) {
      const grid = this.grid;
      grid.innerHTML = payload.grid;

      const count = this.count;
      if (count && payload.count != null) count.innerHTML = payload.count;

      grid.classList.remove(this.LOADING_CLASS);
      grid.removeAttribute('aria-busy');
      this.setActive(handle);
      this.activeHandle = handle;
      this.announce(handle);

      if (updateHistory) this.updateHistory(handle);

      // Let the theme re-init lazy media, row-fit, quick-view, etc. on the new cards.
      if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, {
          responseText: payload.responseText,
        });
      }
    }

    // Announce the filtered result to screen readers (WCAG 4.1.3 status message).
    announce(handle) {
      if (!this.status) return;
      const countText = this.count ? this.count.textContent.trim().replace(/\s+/g, ' ') : '';
      let label = '';
      if (handle) {
        const active = this.triggers.find((trigger) => trigger.dataset.collectionFilter === handle);
        if (active) label = (active.getAttribute('aria-label') || active.textContent || '').trim();
      }
      const message = [label, countText].filter(Boolean).join(', ');
      if (message) this.status.textContent = message;
    }

    setActive(handle) {
      this.triggers.forEach((trigger) => {
        const isActive = trigger.dataset.collectionFilter === handle;
        if (isActive) {
          trigger.setAttribute('aria-current', 'true');
        } else {
          trigger.removeAttribute('aria-current');
        }
        const card = trigger.closest('.collection-card') || trigger;
        card.classList.toggle('collection-card--filter-active', isActive);
      });
      this.classList.toggle('has-active-filter', Boolean(handle));
    }

    updateHistory(handle) {
      const url = new URL(window.location.href);
      if (handle) {
        url.searchParams.set('category', handle);
        window.history.pushState({ category: handle }, '', url.toString());
      } else {
        // Back to "all" — drop the param entirely.
        window.history.pushState({ category: null }, '', this.basePath);
      }
    }
  }

  customElements.define('collection-card-filter', CollectionCardFilter);
}
